import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import classnames from 'classnames';

import { useRWD } from 'hooks';
import {
  useGetHomeSubelementsTypes,
  useGetHomeSubelementItems,
  useGetHomeSubelementItemsMutation
} from 'api';
import { ProductSlider } from 'components/containers';
import { Container, Loader } from 'components/controls';

import styles from 'theme/pages/Home/components/Tabs/Tabs.module.scss';

const ITEMS_PER_PAGE = 6;

const HomeSubelements = () => {
  // Aktualnie wybrany typ subelementu
  const [activeSubelementId, setActiveSubelementId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [accumulatedProducts, setAccumulatedProducts] = useState<any[]>([]);
  const [hasMorePages, setHasMorePages] = useState(false);

  const lastFetchedProductIdsRef = useRef<string>('');
  const lastLoadedPageRef = useRef<number>(0);

  const { isMobile, isDesktop } = useRWD();

  // Pobranie typów subelementów
  const { data: homeSubelementsTypesData } = useGetHomeSubelementsTypes(
    {
      page: 1,
      limit: 999
    },
    {
      onSuccess: (data) => {
        setActiveSubelementId(data.items[0]?.id || null);
      }
    }
  );

  // Pobranie listy produktów po zmianie/wybraniu typu subelementu
  const { data: homeSubelementItemsSimpleData, isLoading: isHomeSubelementItemsLoading, isFetching: isHomeSubelementFetching } =
    useGetHomeSubelementItems(
      activeSubelementId || 0,
      {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        scopes:
          'BasicProductData|ImageFormattedProductData|UnitProductData|StockFormattedProductData'
      },
      {
        enabled: !!activeSubelementId,
        refetchOnMount: true,
        refetchOnWindowFocus: false
      }
    );

  // Pobranie listy produktów
  const { data: homeSubelementItemsFullData, mutate: refetchProductsFullData, isLoading: isHomeSubelementItemsFullLoading } =
    useGetHomeSubelementItemsMutation(activeSubelementId || 0);

  // Resetuj paginację przy zmianie typu subelementu
  useEffect(() => {
    setCurrentPage(1);
    setAccumulatedProducts([]);
    setHasMorePages(false);
    lastFetchedProductIdsRef.current = '';
    lastLoadedPageRef.current = 0;
  }, [activeSubelementId]);

  // Aktualizuj akumulowane produkty i pobierz pełne dane gdy przyjdą nowe dane
  useEffect(() => {
    if (!homeSubelementItemsSimpleData?.items) return;

    // Sprawdź czy ta strona nie została już załadowana
    if (lastLoadedPageRef.current === currentPage) return;

    const newItems = homeSubelementItemsSimpleData.items;

    // Aktualizuj akumulowane produkty
    setAccumulatedProducts((prev) => {
      if (currentPage === 1) {
        // Pierwsza strona - zastąp wszystkie produkty
        lastLoadedPageRef.current = currentPage;
        return newItems;
      } else {
        // Kolejne strony - dodaj tylko nowe produkty (bez duplikatów)
        const existingIds = new Set(prev.map(item => item.id));
        const uniqueNewItems = newItems.filter(item => !existingIds.has(item.id));
        lastLoadedPageRef.current = currentPage;
        return [...prev, ...uniqueNewItems];
      }
    });

    // Sprawdź czy są jeszcze dostępne strony
    const totalPages = homeSubelementItemsSimpleData.total_pages || 0;
    setHasMorePages(currentPage < totalPages);
  }, [homeSubelementItemsSimpleData, currentPage]);

  // Pobierz pełne dane produktów gdy zmienią się akumulowane produkty
  useEffect(() => {
    if (accumulatedProducts.length === 0) return;

    const allProductIds = accumulatedProducts
      .map((item) => item.id)
      .filter((id, index, self) => self.indexOf(id) === index) // Usuń duplikaty
      .sort((a, b) => a - b) // Sortuj dla spójności
      .toString()
      .replaceAll(',', '|');

    // Sprawdź czy ID produktów się zmieniły, aby uniknąć niepotrzebnych wywołań
    if (lastFetchedProductIdsRef.current === allProductIds) return;

    lastFetchedProductIdsRef.current = allProductIds;

    refetchProductsFullData({
      scopes: 'PriceProductData',
      productIds: allProductIds,
      page: 1,
      limit: 999
    });
  }, [accumulatedProducts, refetchProductsFullData]);

  // Funkcja do ładowania kolejnej strony
  const handleLoadMore = useCallback(() => {
    if (!isHomeSubelementItemsLoading && hasMorePages) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [isHomeSubelementItemsLoading, hasMorePages]);

  const homeSubelementsData = useMemo(() => {
    return accumulatedProducts?.map((simpleProduct) => {
      return homeSubelementItemsFullData
        ? {
            ...simpleProduct,
            ...homeSubelementItemsFullData?.items.find(
              (fullProduct) => fullProduct.id === simpleProduct.id
            )
          }
        : simpleProduct;
    });
  }, [accumulatedProducts, homeSubelementItemsFullData]);

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-Home-components-Tabs')}>
      {homeSubelementsTypesData && (
        <div className={styles.productTabsWrapper}>
          <div className={styles.tabsBar}>
            <Container>
              <div className={styles.tabs}>
                {homeSubelementsTypesData.items.map((item) => (
                  <div
                    key={item.id}
                    className={classnames(styles.tab, {
                      [styles.active]: item.id === activeSubelementId
                    })}
                    onClick={() => {
                      setActiveSubelementId(item.id);
                    }}>
                    {item.name}
                  </div>
                ))}
              </div>
            </Container>
          </div>
          <Container>
            <div className={styles.sliderWrapper} style={{ position: 'relative' }}>
              {(homeSubelementsData?.length || 0) > 0 && (
                <>
                  <ProductSlider
                    products={homeSubelementsData || []}
                    isUpdating={ isHomeSubelementItemsFullLoading || isHomeSubelementFetching}
                    itemsPerSlide={isMobile ? 1 : isDesktop ? 4 : 6}
                    onLoadMore={handleLoadMore}
                    hasMore={hasMorePages}
                  />
                  {(isHomeSubelementItemsLoading || isHomeSubelementFetching) && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      zIndex: 10
                    }}>
                      <Loader />
                    </div>
                  )}
                </>
              )}
              {!homeSubelementsData?.length && isHomeSubelementItemsLoading && <Loader />}
            </div>
          </Container>
        </div>
      )}
    </div>
  );
};

export default HomeSubelements;
